// Starting copy for the admin outreach modal. These are drafts to edit before
// sending, not templates to fire as-is — the whole point of these emails is to
// get a reply, and a reply comes from a real question about a real job.
//
// Plain text on purpose. Keep them short enough to read on a phone between
// service calls.

export interface OutreachContext {
  firstName: string;
  /** Project name, or the customer address if the project is unnamed. */
  jobLabel: string;
  daysIdle: number;
  /** The latest request's route, when the bucket reads differently by route. */
  consentRoute?: 'email_request' | 'self_download' | 'customer_request' | null;
}

export interface OutreachDraft {
  subject: string;
  body: string;
}

function days(n: number): string {
  if (n <= 1) return 'yesterday';
  if (n < 14) return `${n} days ago`;
  if (n < 60) return `about ${Math.round(n / 7)} weeks ago`;
  return `about ${Math.round(n / 30)} months ago`;
}

function greeting(firstName: string): string {
  return firstName ? `Hi ${firstName},` : 'Hi,';
}

export const NEXT_STEP_LABELS: Record<string, string> = {
  authorized_no_data: 'Customer authorized — no data arrived',
  utility_not_replied: 'Sent to the utility contact (pilot) — no reply yet',
  homeowner_not_uploaded: 'Download link sent — no file yet',
  authorization_expired: 'Link expired, never answered',
  awaiting_authorization: 'Awaiting customer authorization',
  no_data_never_requested: 'No existing load yet, no data requested',
  needs_equipment: 'Existing load in, no new load added',
  needs_customer_details: 'Load in, customer details blank',
  report_ready: 'Report-ready, not marked complete',
  never_created_project: 'Never created a project',
  none: 'Nothing outstanding',
};

export function buildDraft(nextStep: string, ctx: OutreachContext): OutreachDraft {
  const { firstName, jobLabel, daysIdle, consentRoute } = ctx;
  const hi = greeting(firstName);

  switch (nextStep) {
    // Utility contact pilot: the wait is on the utility's contact, not the
    // contractor, so the draft is a heads-up that Jake is chasing it.
    case 'utility_not_replied':
      return {
        subject: `Still waiting on the utility for ${jobLabel}`,
        body: [
          hi,
          '',
          `The data request for ${jobLabel} went to the utility ${days(daysIdle)}, and nothing has come back yet. I am following up with them directly. When the file arrives it loads into the project and you get a copy by email.`,
          '',
          'Jake',
        ].join('\n'),
      };

    case 'authorized_no_data':
      return {
        subject: `Did the utility data ever come through for ${jobLabel}?`,
        body: [
          hi,
          '',
          `Your customer authorized the data release for ${jobLabel} ${days(daysIdle)}, and I can see the project still has no interval data on it. That is not how it is supposed to go.`,
          '',
          'Did the utility ever send anything back? If you got a file and it would not load, forward it to me and I will look at it myself.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'homeowner_not_uploaded':
      if (consentRoute === 'customer_request') {
        return {
          subject: `Your customer has not requested their usage data for ${jobLabel} yet`,
          body: [
            hi,
            '',
            `You sent your customer the request link for ${jobLabel} ${days(daysIdle)}, and they have not told us they submitted the request yet. On this route they ask the utility themselves, on the utility's own form, which the link opens already filled in. It takes a few minutes, so a quick text now usually does it.`,
            '',
            'Once they submit it and press "I submitted the form", the utility sends the data straight to you. The link stays open for 30 days from the send.',
            '',
            'If they are stuck on the utility form, tell me which utility and what they saw. I would rather hear about it than have the job stall.',
            '',
            'Jake',
          ].join('\n'),
        };
      }
      return {
        subject: `Your customer has not sent their usage file for ${jobLabel} yet`,
        body: [
          hi,
          '',
          `You sent your customer the download link for ${jobLabel} ${days(daysIdle)}, and it is still waiting for their utility data file. Most people who are going to do it do it the same day, so a quick text or call now is worth more than one next week.`,
          '',
          'They already have everything they need: the link in their email shows the steps for their utility and takes the upload, and sending the file is their authorization. It stays open for 14 days from the send.',
          '',
          'If they are stuck on the utility site, tell me which utility and what they saw. The steps are new and I fix them the day I hear about a wrong one.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'authorization_expired':
      if (consentRoute === 'customer_request') {
        return {
          subject: `The request link for ${jobLabel} has closed`,
          body: [
            hi,
            '',
            `You sent your customer the request link for ${jobLabel} ${days(daysIdle)} and they never submitted the utility's request form. The link is good for 30 days, so it has since closed.`,
            '',
            'Send a fresh link from Get data on the Existing Load tab. If the utility already sent you the file, upload it on the Existing Load tab instead.',
            '',
            'If something on the utility form stopped them, tell me which utility. I would rather fix that than watch it happen again.',
            '',
            'Jake',
          ].join('\n'),
        };
      }
      if (consentRoute === 'self_download') {
        return {
          subject: `The download link for ${jobLabel} has closed`,
          body: [
            hi,
            '',
            `You sent your customer the download link for ${jobLabel} ${days(daysIdle)} and no file ever came back. The link is good for 14 days, so it has since closed — if they go looking for that email now, it will not work.`,
            '',
            'Send a fresh link from Get data on the Existing Load tab and give them a heads up that it is coming. A text the same day usually does it.',
            '',
            'If they got stuck on the utility site, tell me which utility and what they saw. I would rather fix the steps than watch it happen again.',
            '',
            'Jake',
          ].join('\n'),
        };
      }
      return {
        subject: `The authorization link for ${jobLabel} expired`,
        body: [
          hi,
          '',
          `You sent an authorization request for ${jobLabel} ${days(daysIdle)} and your customer never signed it. The link is only good for 72 hours, so it has since expired — if they go looking for that email now, it will not work.`,
          '',
          'Send a fresh request and give them a heads up that it is coming. Most people sign the same day they see it or not at all.',
          '',
          'Is there anything about that step that is getting in the way? I would rather fix it than watch it happen again.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'awaiting_authorization':
      return {
        subject: `Your customer has not signed yet — ${jobLabel}`,
        body: [
          hi,
          '',
          `The authorization request for ${jobLabel} has not been signed yet. Worth a quick call today if you can — the link expires 72 hours after it goes out, and nearly everyone who signs does it the same day they get the email.`,
          '',
          'If it lapses, just send a new one. Shout if you hit a snag.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'no_data_never_requested':
      return {
        subject: `Getting utility data for ${jobLabel}`,
        body: [
          hi,
          '',
          `You started ${jobLabel} ${days(daysIdle)} and it is still waiting on utility data. That is the step almost everyone stalls on, so you are in good company.`,
          '',
          'Two ways through it. If you already have an interval data file from the utility, drag it straight into the project. If you do not, Get data on the Existing Load tab sends your customer one link — for most utilities they download their own usage file and upload it right there, and it gives you the twelve months of data the calculation needs.',
          '',
          'And if utility data is not on the table for this job, the traditional calculation on the Existing Load tab needs none of it: the floor area and the appliance list, and the permit-ready number is a few minutes away.',
          '',
          'If you tried any of these and it did not work, tell me what happened. That is the part I most want to hear about.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'needs_equipment':
      return {
        subject: `${jobLabel} is one step from an answer`,
        body: [
          hi,
          '',
          `${jobLabel} already has its existing load established, which is the hard part. All that is left is adding the new load — the heat pump, the EV charger, whatever you are sizing for — and TrueLoad will tell you whether the existing service carries it.`,
          '',
          'It takes about a minute. If something about that screen is unclear, say so and I will fix it.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'needs_customer_details':
      return {
        subject: `${jobLabel} needs a few details before it will print clean`,
        body: [
          hi,
          '',
          `${jobLabel} has the existing load and the new load in it, so the calculation is done. It is missing some of the customer and preparer details, though, and until those are filled in the report prints with a DRAFT — INCOMPLETE watermark that an AHJ will not accept.`,
          '',
          'It is a couple of minutes of typing. Open the project and anything still required is called out on screen.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'report_ready':
      return {
        subject: `Did ${jobLabel} get you what you needed?`,
        body: [
          hi,
          '',
          `${jobLabel} looks finished on my end — the data is in, the load is added, and the report is ready to print.`,
          '',
          'Did it give you what you needed? I am especially interested if you took it to an AHJ, and even more interested if something about it did not hold up.',
          '',
          'Either way I would like to know. A one-line reply is plenty.',
          '',
          'Jake',
        ].join('\n'),
      };

    case 'never_created_project':
      return {
        subject: 'Getting started with TrueLoad',
        body: [
          hi,
          '',
          'You set up a TrueLoad account but have not run a job through it yet. If you have a service upgrade question sitting on your desk right now, that is the one to try it on.',
          '',
          'There is a guided demo under Help that walks the whole thing end to end on a sample job — about five minutes, and it uses the real app rather than screenshots.',
          '',
          'If something stopped you the first time, I would genuinely like to know what it was.',
          '',
          'Jake',
        ].join('\n'),
      };

    default:
      return {
        subject: 'Checking in',
        body: [hi, '', '', 'Jake'].join('\n'),
      };
  }
}
